import { useState, useEffect } from 'react';
import { createAPI } from 'edge-stream-js';
import type { GroupInfo } from 'edge-stream-js';
import { useToast, ChatWindowEdgeStream } from 'edge-stream-js-react';
import { FloatingActivityStreamPanel, ErrorBoundary } from './components';
import { ServerGroupTreePanel, type ServerWithGroups, type ChatGroup } from '@edge-stream/app-ui-components-react';
import { BuildingIcon, ClipboardIcon, BarChartIcon, ChatIcon } from '@bizfirst/common-react';

interface ChatPageProps {
  apiBaseUrl: string;
  currentUser?: string;
  accessToken?: string;
}

export function ChatPage({ apiBaseUrl, currentUser = 'Inspector User', accessToken }: ChatPageProps) {
  const api = createAPI(apiBaseUrl, accessToken ? () => accessToken : undefined);
  const toast = useToast();
  const [servers, setServers] = useState<ServerWithGroups[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ChatGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [showActivityStream, setShowActivityStream] = useState(false);

  const hubUrl = `${apiBaseUrl}/hubs/edge-stream`;

  // Load all servers and their groups on mount
  useEffect(() => {
    loadServerData();
  }, []);

  const loadServerData = async () => {
    setLoading(true);
    try {
      const serverList = await api.getServers();

      if (serverList.length === 0) {
        setServers([]);
        return;
      }

      const allMetadata = await api.getAllServerMetadata();
      const metadataMap = new Map(allMetadata.map(m => [m.serverId, m]));

      const serversWithGroups: ServerWithGroups[] = [];

      for (const server of serverList) {
        try {
          const groups = await api.getGroupsByServer(server.serverId);
          const metadata = metadataMap.get(server.serverId);

          serversWithGroups.push({
            serverId: server.serverId,
            serverName: metadata?.serverName || server.serverId,
            serverDescription: metadata?.serverDescription,
            groups,
            isExpanded: false,
          });
        } catch (serverError) {
          console.error(`Error loading groups for server ${server.serverId}:`, serverError);
          toast.error(`Failed to load groups for server ${server.serverId}`);
        }
      }

      setServers(serversWithGroups);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_NAME_NOT_RESOLVED')) {
        toast.error(`Cannot connect to backend at ${apiBaseUrl} - Is the API running?`);
      } else if (errorMessage.includes('404')) {
        toast.error('API endpoints not found (404) - Check backend configuration');
      } else {
        toast.error(`Failed to load servers: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectServer = (serverId: string) => {
    setSelectedServerId(serverId);
    setSelectedGroup(null);
  };

  const handleSelectGroup = (serverId: string, group: GroupInfo) => {
    setSelectedServerId(serverId);
    setSelectedGroup({
      serverId,
      groupName: group.groupName,
    });
  };

  const selectedServerData = servers.find(s => s.serverId === selectedServerId);

  return (
    <ErrorBoundary>
      <div className="page-container">
        {!selectedGroup && <h2>Chat</h2>}

        <div style={{ display: 'flex', gap: '20px', height: selectedGroup ? 'calc(100vh - 150px)' : 'calc(100vh - 200px)' }}>
        {/* Server/Group Tree Navigation */}
        <ServerGroupTreePanel
          className="server-group-tree-panel"
          servers={servers}
          selectedServerId={selectedServerId}
          selectedGroup={selectedGroup}
          loading={loading}
          onSelectServer={handleSelectServer}
          onSelectGroup={handleSelectGroup}
        />

        {/* Right Panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!selectedGroup ? (
            selectedServerId && selectedServerData ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                padding: '20px',
                overflow: 'auto',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <span style={{ display: 'flex', alignItems: 'center' }}><BuildingIcon /></span>
                    <h3 style={{ color: 'var(--primary)', margin: 0, fontSize: '24px' }}>
                      {selectedServerData.serverName}
                    </h3>
                  </div>
                  {selectedServerData.serverDescription && (
                    <p style={{ color: 'var(--text-secondary)', margin: '8px 0 0 0', fontSize: '14px' }}>
                      {selectedServerData.serverDescription}
                    </p>
                  )}
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '12px',
                    margin: '12px 0 0 0',
                    fontFamily: 'monospace',
                    background: 'var(--bg-tertiary)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    width: 'fit-content',
                  }}>
                    <strong>ID:</strong> {selectedServerData.serverId}
                  </p>
                </div>

                <div style={{
                  background: 'var(--bg-secondary)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                }}>
                  <h4 style={{
                    color: 'var(--primary)',
                    margin: '0 0 16px 0',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center' }}><ClipboardIcon /></span>
                    Groups ({selectedServerData.groups.length})
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedServerData.groups.length === 0 ? (
                      <p style={{ color: 'var(--text-secondary)', fontSize: '14px', fontStyle: 'italic' }}>
                        No groups in this server
                      </p>
                    ) : (
                      selectedServerData.groups.map((group) => (
                        <div
                          key={`${selectedServerData.serverId}-${group.groupName}`}
                          onClick={() => handleSelectGroup(selectedServerData.serverId, group)}
                          style={{
                            padding: '12px',
                            background: 'var(--bg-tertiary)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            border: '1px solid var(--border)',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center' }}><ChatIcon /></span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: 500,
                              color: 'var(--text-primary)',
                              fontSize: '13px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {group.groupName}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                              {group.memberCount || 0} member{(group.memberCount || 0) !== 1 ? 's' : ''} • Created by {group.createdBy || 'system'}
                            </div>
                            {group.description && (
                              <div style={{
                                fontSize: '12px',
                                color: 'var(--text-secondary)',
                                marginTop: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {group.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="chat-empty-state">
                <p>No server selected</p>
                <p>Select a server from the left panel to get started</p>
              </div>
            )
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Chat Header with Logs Toggle */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-tertiary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><ChatIcon /> {selectedGroup?.groupName}</span>
                </h3>
                <button
                  onClick={() => setShowActivityStream(!showActivityStream)}
                  className={`btn-standard btn-small btn-toggle ${showActivityStream ? 'active' : ''}`}
                  title={showActivityStream ? 'Hide activity stream' : 'Show activity stream panel'}
                >
                  <BarChartIcon /> {showActivityStream ? 'Stream (on)' : 'Stream'}
                </button>
              </div>

              {/* Chat Window */}
              <ChatWindowEdgeStream
                hubUrl={hubUrl}
                serverId={selectedGroup.serverId}
                groupName={selectedGroup.groupName}
                currentUser={currentUser}
              />
            </div>
          )}
        </div>
      </div>

        {/* Floating Activity Stream Panel */}
        <FloatingActivityStreamPanel
          isOpen={showActivityStream}
          onClose={() => setShowActivityStream(false)}
          messages={[]}
        />
      </div>
    </ErrorBoundary>
  );
}
